import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FieldType } from '@ngx-formly/core';
import { UserRolesStore } from '../../../features/admin/user-roles/user-roles.store';
import { FieldRightEnum, type FieldRight } from '../../../features/admin/modules/modules.enum';

type RoleBasedRights = Record<string, FieldRight>;

const FIELD_RIGHT_VALUES = new Set<FieldRight>(Object.values(FieldRightEnum));

function isFieldRight(value: unknown): value is FieldRight {
  return FIELD_RIGHT_VALUES.has(value as FieldRight);
}

function isRoleBasedRights(value: unknown): value is RoleBasedRights {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function addMissingRoleRights(value: unknown, roleIds: readonly string[]): RoleBasedRights {
  const rights = isRoleBasedRights(value) ? { ...value } : {};

  roleIds.forEach(roleId => {
    if (!isFieldRight(rights[roleId])) {
      rights[roleId] = FieldRightEnum.None;
    }
  });

  return rights;
}

@Component({
  selector: 'app-role-based-rights',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatFormFieldModule, MatSelectModule],
  template: `
    <div class="mb-4 w-full">
      @if (props.label) {
        <h4 class="mb-2 font-medium">{{ props.label }}</h4>
      }

      @if (props.description) {
        <p class="mb-3 text-lg text-secondary">{{ props.description }}</p>
      }

      <table class="w-full border-separate border-spacing-0">
        <thead>
          <tr>
            <th class="border-b px-3 py-2 text-left font-medium" scope="col">Rol</th>
            <th class="border-b px-3 py-2 text-left font-medium" scope="col">Rechten</th>
          </tr>
        </thead>
        <tbody>
          @for (userRole of userRoles(); track userRole.id) {
            <tr>
              <th class="border-b px-3 py-2 text-left font-normal" scope="row">{{ userRole.name }}</th>
              <td class="border-b px-3 py-2">
                <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
                  <mat-select
                    [aria-label]="'Rechten voor ' + userRole.name"
                    [disabled]="props.disabled || formControl.disabled"
                    [value]="rightFor(userRole.id)"
                    (selectionChange)="setRight(userRole.id, $event.value)">
                    @for (rightOption of rightOptions; track rightOption.value) {
                      <mat-option [value]="rightOption.value">{{ rightOption.label }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </td>
            </tr>
          } @empty {
            <tr>
              <td class="px-3 py-3 text-lg text-secondary" colspan="2">Geen gebruikersrollen beschikbaar.</td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  `,
})
export class RoleBasedRightsComponent extends FieldType implements OnInit {
  readonly #userRolesStore = inject(UserRolesStore);
  readonly #initialized = signal(false);

  readonly rightOptions: ReadonlyArray<{ value: FieldRight; label: string }> = [
    { value: FieldRightEnum.None, label: 'Geen rechten' },
    { value: FieldRightEnum.ReadOnly, label: 'Alleen lezen' },
    { value: FieldRightEnum.ReadWrite, label: 'Lezen en schrijven' },
  ];

  readonly userRoles = computed(() =>
    this.#userRolesStore
      .userRoles()
      .flatMap(userRole => (userRole.id ? [{ id: userRole.id, name: userRole.name ?? userRole.id }] : []))
  );

  constructor() {
    super();
    effect(() => {
      const userRoles = this.userRoles();
      if (this.#initialized()) {
        this.#addMissingRights(userRoles.map(userRole => userRole.id));
      }
    });
  }

  ngOnInit(): void {
    this.#initialized.set(true);
    this.#addMissingRights(this.userRoles().map(userRole => userRole.id));
  }

  rightFor(userRoleId: string): FieldRight {
    const right = isRoleBasedRights(this.formControl.value) ? this.formControl.value[userRoleId] : undefined;
    return isFieldRight(right) ? right : FieldRightEnum.None;
  }

  setRight(userRoleId: string, right: FieldRight): void {
    if (!isFieldRight(right)) {
      return;
    }

    const rights = addMissingRoleRights(
      { ...(isRoleBasedRights(this.formControl.value) ? this.formControl.value : {}), [userRoleId]: right },
      this.userRoles().map(userRole => userRole.id)
    );

    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
    this.formControl.setValue(rights);
  }

  #addMissingRights(roleIds: readonly string[]): void {
    const currentRights = this.formControl.value;
    const hasAllRights =
      isRoleBasedRights(currentRights) && roleIds.every(roleId => isFieldRight(currentRights[roleId]));

    if (!hasAllRights) {
      this.formControl.setValue(addMissingRoleRights(currentRights, roleIds));
    }
  }
}
